import * as PrismicDOM from 'prismic-dom';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest } from 'rxjs';
import { finalize, startWith, switchMap } from 'rxjs/operators';
import { ChatBotEventService, START_FEEDBACK_PRACTICE_KEY } from '../../shared/chat-bot-event.service';
import { LiveClassFeedbacksService } from '../../shared/live-class-feedbacks/live-class-feedbacks.service';
import { LiveClassSurveyService } from '../../shared/live-class-survey.service';
import { SatisfactionAnswerType } from '../../shared/live-class-survey/live-class-survey-data-types';
import { OeAppPersonService } from '../../shared/oe-app-person.service';
import { MODAL_DATA } from '../../shared/tokens/modal-data.token';
import { Language } from '../../shared/types/language';
import { SurveyId } from '../../shared/types/survey-id.enum';
import { WindowService } from '../../shared/window.service';
import { FeedbackCategoryMessage } from '../feedback-category-message';
import { LiveClassCriteriaList } from '../teacher-feedback-service/live-class-criteria';
import { FeedbackResponseDTO } from '../teacher-feedback-service/private-class-feedback-dto';
import { SelectedSurveyQuestionOptions } from '../teacher-feedback-service/selected-survey-question-options';
import { SurveyQuestionGroup } from '../teacher-feedback-service/survey-question-group';
import { SurveyQuestionsIdMap } from './survey-questions-id-map';
import { LiveClassFeedbackRatingOptionType, LiveClassFeedbackRatingQuestionTypes } from './survey-questions-id.enum';
import { PrismicService } from '../../shared/prismic.service';
import { TranslationService } from '../../shared/translation.service';
import { GTMService } from '../../shared/gtm.service';
import { LiveClassSurveyTrackEvents } from './liveclass-suvery-track-event';
import { ClassType } from '../../shared/types/class-types.enum';

export interface TeacherLiveClassFeedbackData {
  feedbackResponseId: number;
  title?: string;
  viewed?: number | null;
  isFromProgressPage?: boolean;
}

export enum LiveClassTypeTranslation {
  PRIVATE_CLASS = 'lcFeedback.classTypePC',
  GROUP_CLASS = 'lcFeedback.classTypeGC',
}

const leftLogoLangs = [Language.TURKISH];

const aiAfterClassPracticeEnabled = true;

@Component({
  selector: 'lp2-teacher-liveclass-feedback-modal',
  templateUrl: './teacher-liveclass-feedback-modal.component.html',
  styleUrls: ['./teacher-liveclass-feedback-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeacherLiveClassFeedbackModalComponent implements OnInit {
  public feedbackResponse: FeedbackResponseDTO;
  public feedbackResponseCategoryMessages: FeedbackCategoryMessage[] = [];
  public isLoading = false;
  public liveClassTypeTranslation: LiveClassTypeTranslation = null;
  public isLeftLogoLang = false;
  public aiAfterClassPracticeEnabled = aiAfterClassPracticeEnabled;
  public satisfactionControl = new FormControl(null);
  public satisfactionSurveyControl = new FormControl({
    profileQuestionId: null,
    profileQuestionOptionId: null,
    optionalText: null,
  });
  public surveyQuestions: SurveyQuestionGroup[];
  public survey: SelectedSurveyQuestionOptions[] = [];
  public savingSurvey = false;
  public isSavedSurvey = false;
  public isFetchingAnswers = true;
  public isFAQLoading = false;
  public showFAQ = false;
  public FAQcontent: string;
  public isHybrid = true;

  public get showSurvey() {
    return this.satisfactionControl.value === SatisfactionAnswerType.SAD && !this.showFAQ;
  }

  constructor(
    private ngbActiveModal: NgbActiveModal,
    private liveClassFeedbacksService: LiveClassFeedbacksService,
    private oeAppPersonService: OeAppPersonService,
    private chatBotEventService: ChatBotEventService,
    private windowService: WindowService,
    private liveClassSurveyService: LiveClassSurveyService,
    private prismic: PrismicService,
    private translationService: TranslationService,
    private gtmService: GTMService,
    public cdRef: ChangeDetectorRef,
    @Inject(MODAL_DATA) public readonly data: TeacherLiveClassFeedbackData
  ) {
    this.liveClassSurveyService.getSurveyQuestions(SurveyId.AI_FEEDBACK_SURVEY).subscribe((questions) => {
      this.surveyQuestions = questions;
    });

    combineLatest([
      this.satisfactionControl.valueChanges,
      this.satisfactionSurveyControl.valueChanges.pipe(startWith(null)),
    ]).subscribe(([satisfaction, survey]) => {
      this.survey = [];
      this.survey.push(...SurveyQuestionsIdMap[satisfaction]);
      if (satisfaction === SatisfactionAnswerType.HAPPY) {
        this.gtmService.sendGA4Event(LiveClassSurveyTrackEvents.AI_FEEDBACK_thumb_up_submit);
        this.postSurvey();
      }

      if (satisfaction === SatisfactionAnswerType.SAD && survey) {
        this.gtmService.sendGA4Event(LiveClassSurveyTrackEvents.AI_FEEDBACK_thumb_down_click);
        this.survey.push(survey);
      }
    });
  }

  public ngOnInit(): void {
    this.liveClassTypeTranslation = this.data.title.includes('Private Class')
      ? LiveClassTypeTranslation.PRIVATE_CLASS
      : LiveClassTypeTranslation.GROUP_CLASS;

    this.isLeftLogoLang = leftLogoLangs.includes(this.oeAppPersonService.getPersonData().preferredLang as Language);

    this.initFeedback();
  }

  private initFeedback(): void {
    this.getFeedbackResponse$()
      .pipe(
        switchMap((feedbackResponse) => {
          this.setFeedbackResponse(feedbackResponse);
          return this.liveClassSurveyService.getTeacherFeedbackSurvey(this.feedbackResponse.feedbackResponseId);
        })
      )
      .subscribe({
        next: (surveyResponses) => {
          this.processSurveyResponses(surveyResponses);
          this.isFetchingAnswers = false;
          this.cdRef.detectChanges();
        },
        error: () => {
          this.isFetchingAnswers = false;
          this.cdRef.detectChanges();
        },
      });
  }

  private processSurveyResponses(surveyResponses: any[]): void {
    this.handleSatisfactionResponse(surveyResponses);
    this.handleSurveyResponse(surveyResponses);
  }

  private handleSatisfactionResponse(surveyResponses: any[]): void {
    const satisfactionResponse = surveyResponses.find(
      (option) => option.profileQuestionId === LiveClassFeedbackRatingQuestionTypes.RATING
    );

    if (satisfactionResponse) {
      const satisfaction =
        satisfactionResponse.profileQuestionOptionId === LiveClassFeedbackRatingOptionType.RATING_POSITIVE
          ? SatisfactionAnswerType.HAPPY
          : SatisfactionAnswerType.SAD;

      this.satisfactionControl.setValue(satisfaction, { emitEvent: false });
      this.satisfactionControl.disable();
    }
  }

  private handleSurveyResponse(surveyResponses: any[]): void {
    const surveyResponse = surveyResponses.find(
      (option) => option.profileQuestionId === LiveClassFeedbackRatingQuestionTypes.REASONS
    );

    if (surveyResponse) {
      this.satisfactionSurveyControl.setValue(
        {
          profileQuestionId: surveyResponse.profileQuestionId,
          profileQuestionOptionId: surveyResponse.profileQuestionOptionId,
          optionalText: surveyResponse.optionalText,
        },
        { emitEvent: false }
      );
      this.satisfactionSurveyControl.disable();
    }
  }

  private getFeedbackResponse$() {
    this.isLoading = true;
    return this.liveClassFeedbacksService.getLiveClassFeedbackById(this.data.feedbackResponseId).pipe(
      finalize(() => {
        this.isLoading = false;
        this.cdRef.detectChanges();
      })
    );
  }

  private setFeedbackResponse(feedbackResponse: FeedbackResponseDTO) {
    this.feedbackResponse = feedbackResponse;
    this.feedbackResponseCategoryMessages = this.groupMessagesOfSameCategory(feedbackResponse.liveClassCriteriaList);
    this.isHybrid = feedbackResponse.classType === ClassType.HYBRID;
    this.cdRef.markForCheck();
  }

  private groupMessagesOfSameCategory(criteriaList: LiveClassCriteriaList[]): FeedbackCategoryMessage[] {
    const criteriaMessageList: FeedbackCategoryMessage[] = [];
    if (!criteriaList) {
      return criteriaMessageList;
    }
    criteriaList.forEach((criteria) => {
      const sameCriteriaMessage = criteriaMessageList.find((cat) => cat.category === criteria.category);
      if (sameCriteriaMessage) {
        sameCriteriaMessage.messages.push(criteria.liveClassCriteriaPerformanceDTO.description);
      } else {
        const newCriteriaMessage: FeedbackCategoryMessage = {
          category: criteria.category,
          messages: [criteria.liveClassCriteriaPerformanceDTO.description],
        };
        criteriaMessageList.push(newCriteriaMessage);
      }
    });
    return criteriaMessageList;
  }

  public postSurvey() {
    if (this.satisfactionControl.value === SatisfactionAnswerType.SAD) {
      this.gtmService.sendGA4Event(LiveClassSurveyTrackEvents.AI_FEEDBACK_thumb_down_submit);
    }
    this.savingSurvey = true;
    this.satisfactionControl.disable({ emitEvent: false });
    this.satisfactionSurveyControl.disable({ emitEvent: false });
    this.liveClassSurveyService
      .saveTeacherFeedbackSurvey(this.survey, this.feedbackResponse.feedbackResponseId)
      .subscribe(() => {
        this.savingSurvey = false;
        this.cdRef.detectChanges();
      });
  }

  public startPractice() {
    const isRootPath = this.windowService.getPathName() === '/';
    const classroomId = this.feedbackResponse.externalClassroomId;

    if (isRootPath) {
      this.chatBotEventService.startPractice(classroomId, 1000);
    } else {
      window.sessionStorage.setItem(START_FEEDBACK_PRACTICE_KEY, classroomId);
      this.windowService.redirectToUrl('/');
    }

    this.close();
  }

  public handleSeeMoreClick(): void {
    if (this.showFAQ) {
      this.closeFAQ();
      return;
    }
    this.gtmService.sendGA4Event(LiveClassSurveyTrackEvents.AI_FEEDBACK_learn_more);
    this.showFAQ = true;
    this.isFAQLoading = true;
    const language = this.translationService.getCurrentLanguage();
    this.prismic
      .query(this.prismic.Predicates.at('my.pc-teacher-feedback.uuid', 'feedback_rating_disclaimer'), language)
      .subscribe((faq) => {
        if (!faq) {
          this.isFAQLoading = false;
          this.cdRef.detectChanges();
          return;
        }
        this.FAQcontent = PrismicDOM.RichText.asHtml(faq[0].data.message);
        this.isFAQLoading = false;
        this.cdRef.detectChanges();
      });
  }

  public closeFAQ(): void {
    this.showFAQ = false;
    this.cdRef.detectChanges();
  }

  public close() {
    this.ngbActiveModal.close();
  }
}



// WEBPACK FOOTER //
// ./ts/app/teacher-feedback/teacher-liveclass-feedback-modal/teacher-liveclass-feedback-modal.component.ts